import { useAtom } from '@reatom/npm-react'
import { Link } from 'react-router'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Typography } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { TradeRoute } from '@root/features/routing'
import { MarkerCapShortFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import type { PairModel } from '@root/entities/pair'
import type { PairCreatorStatsTopToken } from '@root/entities/pair/atoms/reatom-pair-creator-stats'

type Props = {
  pair: PairModel
}

export const DevTokensHighlightToken: React.FC<Props> = ({ pair }) => {
  const { t } = useTranslation()
  const [creatorStats] = useAtom(pair.creatorStats)

  const topTokenByAthMarketCap = useMemo<
    PairCreatorStatsTopToken | undefined
  >(() => {
    if (!creatorStats?.topTokens.length) {
      return undefined
    }
    return creatorStats.topTokens.reduce((acc, token) =>
      token.athMarketCap.isGreaterThan(acc.athMarketCap) ? token : acc,
    )
  }, [creatorStats?.topTokens])

  if (!topTokenByAthMarketCap) {
    return undefined
  }

  const renderContent = () => {
    return (
      <>
        <Key>{t('dev-tokens.ath-mc')}:</Key>
        <ValueContainer>
          <CurrencyIcon
            currency={topTokenByAthMarketCap.currencyModel}
            size={16}
          />
          <TokenName>{topTokenByAthMarketCap.currencyModel.ticker}</TokenName>
          <MutedValue>
            (
            <MarkerCapShortFormatted
              mcap={topTokenByAthMarketCap.athMarketCap}
            />
            )
          </MutedValue>
        </ValueContainer>
      </>
    )
  }

  if (topTokenByAthMarketCap.pairAddress) {
    const href = TradeRoute.build({
      pairAddress: topTokenByAthMarketCap.pairAddress,
      network: topTokenByAthMarketCap.currencyModel.network,
    })
    return (
      <Root as={Link} to={href}>
        {renderContent()}
      </Root>
    )
  }

  return <Root>{renderContent()}</Root>
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 6px;

  display: grid;
`
const Key = styled.span`
  color: var(--text-10);
`
const ValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`

const TokenName = styled(Typography)`
  color: var(--text-12);
  font-weight: 600;
`
const MutedValue = styled.span`
  color: var(--text-11);
`
